import { useState, useEffect } from 'react';
import { collection, doc, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

export const useFirestoreCollection = (collectionName, queryConstraints = []) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    try {
      const collectionRef = collection(db, collectionName);
      const q = queryConstraints.length > 0 
        ? query(collectionRef, ...queryConstraints)
        : collectionRef;

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const documents = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          console.log(`[useFirestoreCollection] ${collectionName}: Fetched ${documents.length} documents`);
          setData(documents);
          setLoading(false);
        },
        (err) => {
          console.error('Firestore error:', err);
          setError(err.message);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } catch (err) {
      console.error('Query setup error:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [collectionName, JSON.stringify(queryConstraints)]);

  return { data, loading, error };
};

export const useFirestoreDoc = (collectionName, docId) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!docId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const docRef = doc(db, collectionName, docId);
    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setData({ id: snapshot.id, ...snapshot.data() });
        } else {
          setData(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Firestore error:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [collectionName, docId]);

  return { data, loading, error };
};

export const usePendingUsers = () => {
  return useFirestoreCollection('users', [
    where('approvalStatus', '==', 'pending'),
    orderBy('createdAt', 'desc')
  ]);
};

export const useApprovedUsers = () => {
  return useFirestoreCollection('users', [
    where('approvalStatus', '==', 'approved'),
    orderBy('name', 'asc')
  ]);
};

export const useAllUsers = () => {
  return useFirestoreCollection('users', [
    orderBy('createdAt', 'desc')
  ]);
};

export const useVIPTokens = () => {
  return useFirestoreCollection('vipTokens', [
    orderBy('createdAt', 'desc')
  ]);
};

export const useWinners = () => {
  return useFirestoreCollection('winners', [
    orderBy('createdAt', 'desc')
  ]);
};

export const useInquiries = () => {
  return useFirestoreCollection('inquiries', [
    orderBy('createdAt', 'desc')
  ]);
};

export const useProducts = () => {
  return useFirestoreCollection('products', [
    orderBy('updatedAt', 'desc')
  ]);
};

export const useUsers = () => {
  return useFirestoreCollection('users', [
    orderBy('createdAt', 'desc')
  ]);
};
